import React, { useState, useEffect, useRef } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const SalesTimelineDashboard = () => {
  // State variables
  const [sampleData, setSampleData] = useState([]);
  const [clientList, setClientList] = useState([]);
  const [range, setRange] = useState([0, 0]);
  const [currentData, setCurrentData] = useState([]);
  const [percentChanges, setPercentChanges] = useState({});
  const [selectedClient, setSelectedClient] = useState('all');
  const [isDraggingLeft, setIsDraggingLeft] = useState(false);
  const [isDraggingRight, setIsDraggingRight] = useState(false);
  const [isDraggingMiddle, setIsDraggingMiddle] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [rangeStartBeforeDrag, setRangeStartBeforeDrag] = useState(0);
  const [rangeEndBeforeDrag, setRangeEndBeforeDrag] = useState(0);
  
  const timelineRef = useRef(null);
  
  const colors = {
    'Client A': '#8884d8',
    'Client B': '#82ca9d',
    'Client C': '#ffc658',
    'Client D': '#ff8042'
  };
  
  // Load data from JSON file
  useEffect(() => {
    fetch('/data/sales-data.json')
      .then(response => response.json())
      .then(jsonData => {
        setSampleData(jsonData.data);
        setClientList(jsonData.clients);
        setRange([0, jsonData.data.length - 1]);
      })
      .catch(error => console.error('Error loading data:', error));
  }, []);
  
  // Update the filtered data and percent changes when the range or sample data changes
  useEffect(() => {
    if (sampleData.length === 0) return;
    
    // Update visible data based on range
    setCurrentData(sampleData.slice(range[0], range[1] + 1));
    
    // Calculate percent changes
    const changes = {};
    
    clientList.forEach(client => {
      const startValue = sampleData[range[0]][client];
      const endValue = sampleData[range[1]][client];
      const percentChange = ((endValue - startValue) / startValue * 100).toFixed(2);
      changes[client] = percentChange;
    });
    
    setPercentChanges(changes);
  }, [range, sampleData, clientList]);
  
  const handleClientChange = (e) => {
    setSelectedClient(e.target.value);
  };
  
  const getPositionPercent = (index) => {
    if (sampleData.length <= 1) return 0;
    return (index / (sampleData.length - 1)) * 100;
  };
  
  const getIndexFromPosition = (positionPercent) => {
    return Math.round((positionPercent / 100) * (sampleData.length - 1));
  };
  
  const handleMouseDown = (e, type) => {
    e.preventDefault();
    if (timelineRef.current) {
      const rect = timelineRef.current.getBoundingClientRect();
      setDragStartX(e.clientX - rect.left);
      setRangeStartBeforeDrag(range[0]);
      setRangeEndBeforeDrag(range[1]);
      
      if (type === 'left') {
        setIsDraggingLeft(true);
      } else if (type === 'right') {
        setIsDraggingRight(true);
      } else if (type === 'middle') {
        setIsDraggingMiddle(true);
      }
    }
  };
  
  const handleMouseMove = (e) => {
    if (!isDraggingLeft && !isDraggingRight && !isDraggingMiddle) return;
    
    if (timelineRef.current) {
      const rect = timelineRef.current.getBoundingClientRect();
      const currentX = e.clientX - rect.left;
      const timelineWidth = rect.width;
      
      const positionPercent = (currentX / timelineWidth) * 100;
      const dragDeltaPercent = ((currentX - dragStartX) / timelineWidth) * 100;
      
      if (isDraggingLeft) {
        const newStartIndex = getIndexFromPosition(positionPercent);
        if (newStartIndex >= 0 && newStartIndex < range[1]) {
          setRange([newStartIndex, range[1]]);
        }
      } else if (isDraggingRight) {
        const newEndIndex = getIndexFromPosition(positionPercent);
        if (newEndIndex > range[0] && newEndIndex < sampleData.length) {
          setRange([range[0], newEndIndex]);
        }
      } else if (isDraggingMiddle) {
        const rangeSize = rangeEndBeforeDrag - rangeStartBeforeDrag;
        let newStartIndex = getIndexFromPosition(
          getPositionPercent(rangeStartBeforeDrag) + dragDeltaPercent
        );
        
        if (newStartIndex < 0) {
          newStartIndex = 0;
        } else if (newStartIndex + rangeSize >= sampleData.length) {
          newStartIndex = sampleData.length - 1 - rangeSize;
        }
        
        setRange([newStartIndex, newStartIndex + rangeSize]);
      }
    }
  };
  
  const handleMouseUp = () => {
    setIsDraggingLeft(false);
    setIsDraggingRight(false);
    setIsDraggingMiddle(false);
  };


  const handleTouchStart = (e, type) => {
    e.preventDefault();
    if (timelineRef.current) {
      const rect = timelineRef.current.getBoundingClientRect();
      const touch = e.touches[0];
      setDragStartX(touch.clientX - rect.left);
      setRangeStartBeforeDrag(range[0]);
      setRangeEndBeforeDrag(range[1]);
      
      if (type === 'left') {
        setIsDraggingLeft(true);
      } else if (type === 'right') {
        setIsDraggingRight(true);
      } else if (type === 'middle') {
        setIsDraggingMiddle(true);
      }
    }
  };
  
  const handleTouchMove = (e) => {
    if (!isDraggingLeft && !isDraggingRight && !isDraggingMiddle) return;
    
    if (timelineRef.current) {
      const rect = timelineRef.current.getBoundingClientRect();
      const touch = e.touches[0];
      const currentX = touch.clientX - rect.left;
      const timelineWidth = rect.width;
      
      const positionPercent = (currentX / timelineWidth) * 100;
      const dragDeltaPercent = ((currentX - dragStartX) / timelineWidth) * 100;
      
      if (isDraggingLeft) {
        const newStartIndex = getIndexFromPosition(positionPercent);
        if (newStartIndex >= 0 && newStartIndex < range[1]) {
          setRange([newStartIndex, range[1]]);
        }
      } else if (isDraggingRight) {
        const newEndIndex = getIndexFromPosition(positionPercent);
        if (newEndIndex > range[0] && newEndIndex < sampleData.length) {
          setRange([range[0], newEndIndex]);
        }
      } else if (isDraggingMiddle) {
        const rangeSize = rangeEndBeforeDrag - rangeStartBeforeDrag;
        let newStartIndex = getIndexFromPosition(
          getPositionPercent(rangeStartBeforeDrag) + dragDeltaPercent
        );
        
        if (newStartIndex < 0) {
          newStartIndex = 0;
        } else if (newStartIndex + rangeSize >= sampleData.length) {
          newStartIndex = sampleData.length - 1 - rangeSize;
        }
        
        setRange([newStartIndex, newStartIndex + rangeSize]);
      }
    }
  };
  
  const handleTouchEnd = () => {
    setIsDraggingLeft(false);
    setIsDraggingRight(false);
    setIsDraggingMiddle(false);
  };
  
  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDraggingLeft, isDraggingRight, isDraggingMiddle, dragStartX, rangeStartBeforeDrag, rangeEndBeforeDrag]);
  
  // If data is still loading, show a loading message
  if (sampleData.length === 0) {
    return <div className="p-6">Loading sales data...</div>;
  }
  
  return (
    <div className="flex flex-col p-6 max-w-5xl mx-auto bg-white rounded-lg shadow-lg">
      <h1 className="text-2xl font-bold mb-6 text-gray-800">Sales Timeline Dashboard</h1>
      
      <div className="mb-8">
        <div className="flex justify-between mb-2">
          <span className="text-gray-600 font-medium">
            Time Range: {sampleData[range[0]]?.month} — {sampleData[range[1]]?.month}
          </span>
          <div className="flex gap-2">
            <label className="text-gray-600 font-medium">Client Filter:</label>
            <select 
              value={selectedClient}
              onChange={handleClientChange}
              className="bg-gray-100 border border-gray-300 rounded px-2"
            >
              <option value="all">All Clients</option>
              {clientList.map(client => (
                <option key={client} value={client}>{client}</option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="px-4 mt-6 mb-2">
          <div 
            ref={timelineRef}
            className="h-10 bg-gray-200 rounded-lg relative cursor-pointer" 
            onMouseDown={(e) => handleMouseDown(e, 'middle')}
            onTouchStart={(e) => handleTouchStart(e, 'middle')}
          >
            {/* Timeline markers */}
            {sampleData.map((item, index) => (
              <div 
                key={index}
                className="absolute top-0 w-px h-3 bg-gray-400" 
                style={{ left: `${getPositionPercent(index)}%` }}
              />
            ))}
            
            {/* Selected range */}
            <div 
              className="absolute top-0 h-full bg-blue-400 opacity-50"
              style={{ 
                left: `${getPositionPercent(range[0])}%`, 
                width: `${getPositionPercent(range[1]) - getPositionPercent(range[0])}%` 
              }}
              onMouseDown={(e) => handleMouseDown(e, 'middle')}
              onTouchStart={(e) => handleTouchStart(e, 'middle')}
            />
            
            {/* Left handle */}
            <div 
              className="absolute top-0 h-full w-3 bg-blue-600 rounded-l-lg cursor-ew-resize"
              style={{ left: `${getPositionPercent(range[0])}%` }}
              onMouseDown={(e) => handleMouseDown(e, 'left')}
              onTouchStart={(e) => handleTouchStart(e, 'left')}
            />
            
            {/* Right handle */}
            <div 
              className="absolute top-0 h-full w-3 bg-blue-600 rounded-r-lg cursor-ew-resize"
              style={{ left: `${getPositionPercent(range[1])}%` }}
              onMouseDown={(e) => handleMouseDown(e, 'right')}
              onTouchStart={(e) => handleTouchStart(e, 'right')}
            />
          </div>
          
          {/* Timeline labels */}
          <div className="flex justify-between mt-1 text-xs text-gray-600">
            <span>{sampleData[0]?.month}</span>
            <span>{sampleData[Math.floor(sampleData.length / 2)]?.month}</span>
            <span>{sampleData[sampleData.length - 1]?.month}</span>
          </div>
          
          {/* Preset range buttons */}
          <div className="flex flex-wrap gap-2 mt-4">
            <button 
              className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm font-medium"
              onClick={() => setRange([sampleData.length - 4, sampleData.length - 1])}
            >
              Last Quarter
            </button>
            <button 
              className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm font-medium"
              onClick={() => {
                // Last 6 months
                const endIndex = sampleData.length - 1;
                const startIndex = endIndex - 5 >= 0 ? endIndex - 5 : 0;
                setRange([startIndex, endIndex]);
              }}
            >
              Last 6 Months
            </button>
            <button 
              className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm font-medium"
              onClick={() => {
                // Last 12 months
                const endIndex = sampleData.length - 1;
                const startIndex = endIndex - 11 >= 0 ? endIndex - 11 : 0;
                setRange([startIndex, endIndex]);
              }}
            >
              Last 12 Months
            </button>
            <button 
              className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm font-medium"
              onClick={() => {
                // Find the start of the current year (Jan)
                const currentYear = new Date().getFullYear();
                const startIndex = sampleData.findIndex(d => d.month.includes(currentYear.toString()) && d.month.includes('Jan'));
                // Current month
                const currentMonth = new Date().getMonth();
                const endIndex = startIndex + currentMonth >= sampleData.length ? sampleData.length - 1 : startIndex + currentMonth;
                setRange([startIndex >= 0 ? startIndex : 0, endIndex]);
              }}
            >
              Year to Date
            </button>
            <button 
              className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm font-medium"
              onClick={() => setRange([0, sampleData.length - 1])}
            >
              All Time
            </button>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-50 p-4 rounded-lg mb-6">
        <h2 className="text-xl font-semibold mb-4 text-gray-700">Percent Change (Selected Period)</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {clientList.map(client => (
            <div 
              key={client}
              className={`p-4 rounded-lg ${
                parseFloat(percentChanges[client] || 0) >= 0 
                  ? 'bg-green-50 border border-green-200' 
                  : 'bg-red-50 border border-red-200'
              }`}
            >
              <h3 className="font-semibold text-gray-700">{client}</h3>
              <p className={`text-2xl font-bold ${
                parseFloat(percentChanges[client] || 0) >= 0 
                  ? 'text-green-600' 
                  : 'text-red-600'
              }`}>
                {percentChanges[client] || '0.00'}%
              </p>
            </div>
          ))}
        </div>
      </div>
      
      <div className="h-80 mb-6">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={currentData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, 'Sales']} />
            <Legend />
            {clientList
              .filter(client => selectedClient === 'all' || selectedClient === client)
              .map(client => (
                <Line 
                  key={client}
                  type="monotone" 
                  dataKey={client} 
                  stroke={colors[client]} 
                  activeDot={{ r: 8 }}
                  strokeWidth={2}
                />
              ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      
      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
        <h2 className="text-lg font-semibold mb-2 text-blue-800">How to use:</h2>
        <ul className="list-disc pl-6 text-blue-700 space-y-1">
          <li>Drag the blue handles to adjust the date range</li>
          <li>Drag the blue selection area to move the entire range</li>
          <li>The percent change cards automatically update with the selected range</li>
          <li>Use the dropdown to filter for specific clients</li>
          <li>Hover over the chart lines to see detailed sales figures</li>
        </ul>
      </div>
    </div>
  );
};

export default SalesTimelineDashboard;